import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fileUrl, errText } from "@/lib/api";
import { GraduationCap, Loader2, CheckCircle2, Camera, UserCheck, ArrowLeft } from "lucide-react";

export default function SelfAttendance() {
  const [params] = useSearchParams();
  const [idInput, setIdInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const submittingRef = useRef(false);

  const submit = async (rawId) => {
    const employee_id = (rawId || "").trim();
    if (!employee_id || submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/attendance/self", { employee_id });
      setResult(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  useEffect(() => {
    const pid = params.get("id");
    if (pid) {
      setIdInput(pid);
      submit(pid);
    }
    // eslint-disable-next-line
  }, []);

  const reset = () => { setResult(null); setError(""); setIdInput(""); };

  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #25499B 0%, transparent 45%), radial-gradient(circle at 85% 80%, #F16522 0%, transparent 45%)" }} />

      <header className="relative flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-orange-500">
            <GraduationCap className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="font-heading font-extrabold text-white leading-tight text-sm">Presensi Mandiri</p>
            <p className="text-[11px] text-slate-400">Web Monitoring Pengembangan Karyawan</p>
          </div>
        </div>
        <Link to="/login" data-testid="back-to-login" className="flex items-center gap-1 text-sm text-slate-300 hover:text-white transition">
          <ArrowLeft className="h-4 w-4" /> Login
        </Link>
      </header>

      <div className="relative flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          {result ? (
            <div className="rounded-3xl bg-white p-8 text-center shadow-2xl">
              <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${result.status === "recorded" ? "bg-emerald-50" : "bg-amber-50"}`}>
                {result.status === "recorded"
                  ? <CheckCircle2 className="h-11 w-11 text-emerald-500" />
                  : <UserCheck className="h-11 w-11 text-amber-500" />}
              </div>
              <h2 className="mt-5 font-heading text-2xl font-extrabold text-slate-900">
                {result.status === "recorded" ? "Kehadiran Tercatat!" : "Sudah Tercatat"}
              </h2>
              <p className="text-sm text-slate-500">
                {result.status === "recorded" ? "Terima kasih, presensi Anda berhasil." : "Anda sudah presensi untuk hari ini."}
              </p>
              <div className="mt-6 flex items-center gap-4 rounded-2xl bg-slate-50 p-4 text-left">
                {result.photo_url
                  ? <img src={fileUrl(result.photo_url)} alt="" className="h-14 w-14 rounded-xl object-cover" />
                  : <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-50 text-xl font-bold text-blue-600">{result.employee_name[0]}</div>}
                <div>
                  <p data-testid="self-result-name" className="font-semibold text-slate-900">{result.employee_name}</p>
                  <p className="text-sm text-slate-500">{result.position || "—"}</p>
                  <span className="mt-1 inline-block rounded-md bg-slate-200 px-2 py-0.5 text-xs font-mono text-slate-600">{result.employee_id}</span>
                </div>
              </div>
              <p className="mt-4 text-sm font-semibold text-slate-700">Tanggal: {result.date}</p>
              <button data-testid="self-reset-button" onClick={reset} className="mt-6 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition">
                Presensi Karyawan Lain
              </button>
            </div>
          ) : (
            <div className="rounded-3xl bg-white p-8 shadow-2xl">
              <h2 className="font-heading text-2xl font-extrabold text-slate-900">Catat Kehadiran</h2>
              <p className="mt-1 text-sm text-slate-500">Masukkan Nomor ID Karyawan Anda untuk mencatat kehadiran.</p>
              <form onSubmit={(e) => { e.preventDefault(); submit(idInput); }} className="mt-6 space-y-4">
                <input
                  data-testid="self-id-input"
                  autoFocus
                  value={idInput}
                  onChange={(e) => setIdInput(e.target.value)}
                  placeholder="mis. EMP001"
                  className="w-full rounded-xl border border-slate-300 px-4 py-3.5 text-center text-lg font-mono tracking-wide outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
                {error && <p data-testid="self-error" className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-600 text-center">{error}</p>}
                <button data-testid="self-submit-button" type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3.5 text-sm font-semibold text-white hover:bg-blue-700 transition disabled:opacity-60">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Catat Kehadiran
                </button>
              </form>
            </div>
          )}
          <p className="mt-6 text-center text-xs text-slate-400 flex items-center justify-center gap-1">
            <Camera className="h-3.5 w-3.5" /> Presensi dicatat untuk tanggal hari ini
          </p>
        </div>
      </div>
    </div>
  );
}
